import { TREASURY_PREVIEW as config } from "../config/treasuryPreview";
import { TreasuryError, treasuryBonus, type TreasuryPayment, type TreasuryPort, type TreasuryPosition, type TreasuryQuote, type TreasurySnapshot } from "../domain/treasury";

type Saved = { version: 1; revision: number; reward: string; grim: string; positions: TreasuryPosition[];
  payments: TreasuryPayment[]; requests: Record<string, string>; examplesLoaded: boolean };
type Options = { now?: () => number; storage?: Pick<Storage, "getItem" | "setItem">; delay?: () => Promise<void> };
const clone = <T>(value: T): T => structuredClone(value);
const iso = (ms: number) => new Date(ms).toISOString();
const gram = (micros: string) => (BigInt(micros) * 1_000_000_000n / BigInt(config.gramPriceMicros)).toString();
const grim = (micros: string) => (BigInt(micros) * 1_000_000_000n / BigInt(config.grimPriceMicros)).toString();
const digits = (value: unknown): value is string => typeof value === "string" && /^\d{1,60}$/.test(value);

/** Isolated browser demonstration. It never calls APIs, connects wallets or spends game funds. */
export class PreviewTreasuryService implements TreasuryPort {
  readonly #now: () => number;
  readonly #storage?: Options["storage"];
  readonly #delay: () => Promise<void>;
  #data: Saved;
  #quotes = new Map<string, TreasuryQuote>();
  #listeners = new Set<(state: TreasurySnapshot) => void>();
  #timer: ReturnType<typeof setInterval> | undefined;
  constructor(options: Options = {}) {
    this.#now = options.now ?? Date.now; this.#storage = options.storage;
    this.#delay = options.delay ?? (() => Promise.resolve());
    this.#data = { version: 1, revision: 0, reward: config.rewardMicros, grim: config.grimNano,
      positions: [], payments: [], requests: {}, examplesLoaded: false };
    try {
      const raw = JSON.parse(this.#storage?.getItem(config.storageKey) ?? "null") as Saved | null;
      if (raw?.version === 1 && Number.isSafeInteger(raw.revision) && digits(raw.reward) && digits(raw.grim)
        && Array.isArray(raw.positions) && raw.positions.length <= 200 && Array.isArray(raw.payments) && raw.payments.length <= 600
        && typeof raw.requests === "object" && raw.requests !== null
        && raw.positions.every(p => typeof p.id === "string" && digits(p.plan?.nominalMicros) && digits(p.initialMicros) && digits(p.depositedGrimNano)
          && typeof p.walletLabel === "string" && Number.isSafeInteger(p.plan.durationSeconds) && p.plan.durationSeconds > 0
          && [p.plan.initialBps, p.plan.mentorBps, p.plan.protocolBps].every(v => Number.isSafeInteger(v) && v >= 0 && v <= 10000)
          && ["confirming", "active", "settling", "completed"].includes(p.status)
          && (!p.initialPaidAt || Number.isFinite(Date.parse(p.initialPaidAt))) && (!p.principalPaidAt || Number.isFinite(Date.parse(p.principalPaidAt)))
          && Number.isFinite(Date.parse(p.openedAt)) && Number.isFinite(Date.parse(p.maturesAt)))
        && raw.payments.every(p => typeof p.id === "string" && typeof p.positionId === "string" && ["initial", "principal", "mentor"].includes(p.kind)
          && digits(p.gramNano) && digits(p.usdtMicros) && Number.isFinite(Date.parse(p.paidAt)))) this.#data = raw;
    } catch { /* A damaged preview must not affect the game or its real accounts. */ }
  }
  #persist() {
    try { this.#storage?.setItem(config.storageKey, JSON.stringify(this.#data)); }
    catch { throw new TreasuryError("STORAGE"); }
  }
  #payment(position: TreasuryPosition, kind: "initial" | "principal", at: string) {
    const id = `${position.id}:${kind}`;
    if (this.#data.payments.some(p => p.id === id)) return;
    const usdtMicros = kind === "initial" ? position.initialMicros : position.plan.nominalMicros;
    this.#data.payments.push({ id, positionId: position.id, kind, usdtMicros, gramNano: gram(usdtMicros), paidAt: at });
    if (kind === "initial") position.initialPaidAt = at; else position.principalPaidAt = at;
  }
  #snapshot(): TreasurySnapshot {
    const backup = clone(this.#data);
    const before = JSON.stringify(this.#data.positions);
    for (const p of this.#data.positions) {
      if (!p.initialPaidAt && this.#now() >= Date.parse(p.openedAt) + config.confirmationMs) this.#payment(p, "initial", iso(Date.parse(p.openedAt) + config.confirmationMs));
      if (!p.principalPaidAt && this.#now() >= Date.parse(p.maturesAt) + config.confirmationMs) this.#payment(p, "principal", iso(Date.parse(p.maturesAt) + config.confirmationMs));
      p.status = p.principalPaidAt ? "completed" : !p.initialPaidAt ? "confirming" : this.#now() >= Date.parse(p.maturesAt) ? "settling" : "active";
    }
    if (before !== JSON.stringify(this.#data.positions)) {
      this.#data.revision++;
      try { this.#persist(); } catch (error) { this.#data = backup; throw error; }
    }
    return clone({ mode: "preview", purchasesEnabled: true, serverNow: iso(this.#now()), revision: this.#data.revision,
      plans: config.plans.map(p => ({ ...p, indicativeGrimNano: grim(p.nominalMicros), indicativePriceAt: iso(this.#now()) })), rewardAvailableMicros: this.#data.reward, grimAvailableNano: this.#data.grim,
      walletLabel: "preview-wallet", positions: [...this.#data.positions].reverse(), payments: [...this.#data.payments].sort((a, b) => b.paidAt.localeCompare(a.paidAt)) });
  }
  async read() { await this.#delay(); return this.#snapshot(); }
  async quote(planId: string) {
    await this.#delay();
    const plan = config.plans.find(p => p.id === planId && p.available); if (!plan) throw new TreasuryError("PLAN");
    const bonus = treasuryBonus(plan);
    if (BigInt(this.#data.reward) < BigInt(bonus)) throw new TreasuryError("BALANCE");
    if (BigInt(this.#data.grim) < BigInt(grim(plan.nominalMicros))) throw new TreasuryError("GRIM");
    for (const [id, q] of this.#quotes) if (Date.parse(q.expiresAt) <= this.#now()) this.#quotes.delete(id);
    const quote: TreasuryQuote = { id: crypto.randomUUID(), plan: clone(plan), expiresAt: iso(this.#now() + config.quoteLifetimeMs),
      grimNano: grim(plan.nominalMicros), rewardDebitMicros: bonus, initialGramNano: gram(bonus),
      networkBudgetNano: config.networkBudgetNano, walletLabel: "preview-wallet" };
    this.#quotes.set(quote.id, quote); return clone(quote);
  }
  async open(quoteId: string, requestId: string) {
    await this.#delay();
    const existing = this.#data.positions.find(p => p.id === this.#data.requests[requestId]); if (existing) return clone(existing);
    const quote = this.#quotes.get(quoteId); if (!quote || Date.parse(quote.expiresAt) <= this.#now()) throw new TreasuryError("EXPIRED");
    if (BigInt(this.#data.reward) < BigInt(quote.rewardDebitMicros)) throw new TreasuryError("BALANCE");
    if (BigInt(this.#data.grim) < BigInt(quote.grimNano)) throw new TreasuryError("GRIM");
    if (this.#data.positions.length >= 200) throw new TreasuryError("UNAVAILABLE");
    const backup = clone(this.#data);
    const position: TreasuryPosition = { id: `GR-${String(this.#data.positions.length + 1).padStart(4, "0")}`,
      plan: clone(quote.plan), depositedGrimNano: quote.grimNano, initialMicros: quote.rewardDebitMicros,
      openedAt: iso(this.#now()), maturesAt: iso(this.#now() + quote.plan.durationSeconds * 1000),
      initialPaidAt: null, principalPaidAt: null, walletLabel: quote.walletLabel, status: "confirming" };
    this.#data.reward = (BigInt(this.#data.reward) - BigInt(quote.rewardDebitMicros)).toString();
    this.#data.grim = (BigInt(this.#data.grim) - BigInt(quote.grimNano)).toString();
    this.#data.positions.push(position); this.#data.requests[requestId] = position.id; this.#data.revision++;
    try { this.#persist(); } catch (error) { this.#data = backup; throw error; }
    this.#quotes.delete(quoteId); this.#emit(); return clone(position);
  }
  subscribe(listener: (state: TreasurySnapshot) => void) {
    this.#listeners.add(listener);
    this.#timer ??= setInterval(() => { try { this.#emit(); } catch { /* read() surfaces storage errors. */ } }, 1000);
    return () => { this.#listeners.delete(listener); if (!this.#listeners.size) { clearInterval(this.#timer); this.#timer = undefined; } };
  }
  #emit() { const value = this.#snapshot(); for (const listener of this.#listeners) listener(value); }
  async showExamples() {
    if (this.#data.examplesLoaded) return;
    const backup = clone(this.#data);
    const now = this.#now();
    const examples = [{ plan: config.plans[1], days: 12 }, { plan: config.plans[0], days: 30 }, { plan: config.plans[2], days: 35 }];
    for (const [index, { plan, days }] of examples.entries()) {
      if (!plan) continue;
      const opened = now - days * 86400_000;
      const p: TreasuryPosition = { id: `EX-${index + 1}`, plan: clone(plan), depositedGrimNano: grim(plan.nominalMicros), initialMicros: treasuryBonus(plan),
        openedAt: iso(opened), maturesAt: iso(opened + plan.durationSeconds * 1000), initialPaidAt: null, principalPaidAt: null, walletLabel: "preview-wallet", status: "active" };
      this.#data.positions.push(p); this.#payment(p, "initial", iso(opened + config.confirmationMs));
    }
    this.#data.examplesLoaded = true; this.#data.revision++;
    try { this.#persist(); this.#emit(); } catch (error) { this.#data = backup; throw error; }
  }
}
